import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import type {
  CreateTeamMemberPayload,
  SyncAccessResult,
  TeamEntityType,
  TeamMember,
  UpdateTeamMemberPayload,
} from "../types";

const KEY = ["teams"] as const;

function teamKey(entityType: TeamEntityType, entityId: string) {
  return [...KEY, entityType, entityId] as const;
}

export function useTeam(
  entityType: TeamEntityType | null,
  entityId: string | null,
): UseQueryResult<TeamMember[]> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [
      ...KEY,
      entityType ?? "none",
      entityId ?? "none",
      accessToken ? "auth" : "anon",
    ],
    enabled: !!accessToken && !!entityType && !!entityId,
    queryFn: async () => {
      const res = await apiFetch<{ data: TeamMember[] }>("/teams", {
        token: accessToken,
        query: { entityType: entityType!, entityId: entityId! },
      });
      return res.data;
    },
  });
}

export function useAddTeamMember(entityType: TeamEntityType, entityId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateTeamMemberPayload) =>
      apiFetch<{ member: TeamMember }>("/teams", {
        method: "POST",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: teamKey(entityType, entityId) });
    },
  });
}

export function useUpdateTeamMember(
  entityType: TeamEntityType,
  entityId: string,
) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdateTeamMemberPayload;
    }) =>
      apiFetch<{ member: TeamMember }>(`/teams/${id}`, {
        method: "PATCH",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: teamKey(entityType, entityId) });
    },
  });
}

export function useRemoveTeamMember(
  entityType: TeamEntityType,
  entityId: string,
) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ success: true }>(`/teams/${id}`, {
        method: "DELETE",
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: teamKey(entityType, entityId) });
    },
  });
}

export function useSyncTeamAccess() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { entityType: TeamEntityType; entityId: string }) =>
      apiFetch<SyncAccessResult>("/teams/sync-access", {
        method: "POST",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
    },
  });
}
